import type { Writing, WritingBlock } from "../types";

const body: WritingBlock[] = [
  { type: "blockquote", text: "Minimalism is not taste itself." },
  { type: "heading", text: "Taste Is Everywhere Now" },
  {
    type: "paragraph",
    text: "Taste has become one of Silicon Valley’s favorite words.",
  },
  {
    type: "paragraph",
    text: "It has become the antidote to AI vibecode aesthetics. As more products become easier to build, taste is increasingly treated as the thing that separates good work from generated work. Anyone can produce an interface. Fewer people can make one feel considered.",
  },
  { type: "paragraph", text: "I mostly agree with this." },
  {
    type: "paragraph",
    text: "But I think the way people talk about taste has become too narrow.",
  },
  { type: "heading", text: "Taste, or センス" },
  {
    type: "paragraph",
    text: "In Japanese, the closest word to taste is probably センス.",
  },
  {
    type: "paragraph",
    text: "But the way we use センス is interesting. We don’t usually talk about it like a ranking. We say:",
  },
  { type: "paragraph", text: "センスがある", bold: true },
  { type: "paragraph", text: "You have sense. You have taste." },
  { type: "paragraph", text: "Or:" },
  { type: "paragraph", text: "センスがない", bold: true },
  { type: "paragraph", text: "You don’t have sense. You don’t have taste." },
  { type: "paragraph", text: "That small difference matters." },
  {
    type: "paragraph",
    text: "Online, people often talk about “high taste” and “low taste,” as if taste exists on a vertical ladder. Some work is supposedly more tasteful because it is quieter, cleaner, more refined, more minimal. Other work is treated as less tasteful because it is colorful, playful, loud, strange, or emotionally obvious.",
  },
  {
    type: "paragraph",
    text: "But センス does not really work that way. At least to me, it feels closer to a binary than qualitative.",
  },
  { type: "paragraph", text: "You either have it or you don’t." },
  {
    type: "paragraph",
    text: "Not because taste is objective. Not because everyone should agree on the same style. But because taste is less about where something sits on an aesthetic hierarchy, and more about whether there is judgment behind it.",
  },
  { type: "heading", text: "Why “High Taste” Feels Off" },
  {
    type: "paragraph",
    text: "This is why I dislike the phrase “high taste.”",
  },
  {
    type: "paragraph",
    text: "It turns taste into a ladder. And increasingly, the top of that ladder looks very specific.",
  },
  {
    type: "paragraph",
    text: "Minimal. Neutral colors. Left-aligned text. Generous whitespace. Quiet layouts. Probably Inter.",
  },
  {
    type: "paragraph",
    text: "At some point, this became the default signal of taste in product design.",
  },
  {
    type: "paragraph",
    text: "And to be clear, I like this style. A lot of my own work lives inside it. My portfolio and my work at Judgment use many of the same ingredients: restraint, neutral tones, simple typography, and a general preference for not over-designing things.",
  },
  {
    type: "paragraph",
    text: "So this is not a critique of minimalism itself.",
  },
  {
    type: "paragraph",
    text: "It is a critique of minimalism supremacists.",
  },
  { type: "heading", text: "Minimalism Supremacists" },
  {
    type: "paragraph",
    text: "By minimalism supremacists, I mean people who treat restraint as the only serious aesthetic position.",
  },
  {
    type: "paragraph",
    text: "The people who see anything colorful, expressive, playful, loud, mascot-driven, or weird and immediately place it outside the category of taste.",
  },
  {
    type: "paragraph",
    text: "As if taste only exists in off-white backgrounds, gray borders, grids, and one very serious sans-serif.",
  },
  { type: "paragraph", text: "That feels wrong to me." },
  {
    type: "paragraph",
    text: "Minimalism can have taste. But minimalism is not taste itself.",
  },
  { type: "heading", text: "Not Everything Tasteful Looks the Same" },
  { type: "paragraph", text: "A maximalist interface can have taste." },
  { type: "paragraph", text: "A childish illustration style can have taste." },
  { type: "paragraph", text: "A strange color palette can have taste." },
  {
    type: "paragraph",
    text: "Duolingo, for example, may not fit the current “high taste” startup aesthetic. It is colorful, mascot-driven, emotionally loud, and sometimes visually chaotic. But I don’t think that means it lacks taste. It has a clear point of view. It knows what it is. The aesthetic serves the product’s personality.",
  },
  {
    type: "paragraph",
    text: "To me, that is much closer to taste than another perfectly neutral landing page with no real opinion behind it.",
  },
  { type: "heading", text: "Taste Is Judgment" },
  {
    type: "paragraph",
    text: "A lot of “high taste” design online is just a set of surface markers.",
  },
  {
    type: "paragraph",
    text: "Those things can be beautiful. But they can also be default decisions disguised as taste.",
  },
  { type: "paragraph", text: "Quiet design can be lazy." },
  { type: "paragraph", text: "A neutral palette can be thoughtless." },
  { type: "paragraph", text: "Inter can be a default, not a decision." },
  { type: "paragraph", text: "I don’t have a grand conclusion here." },
  { type: "paragraph", text: "Mostly just an observation." },
  {
    type: "paragraph",
    text: "The way people talk about taste right now feels too narrow. We have started treating one aesthetic as the proof of taste, when taste should be about the quality of judgment behind the aesthetic.",
  },
  { type: "paragraph", text: "Taste is not minimalism." },
  { type: "paragraph", text: "Taste is knowing what something should be." },
];

const writing: Writing = {
  slug: "on-taste",
  title: "On Taste",
  date: "2026",
  lastUpdated: "5/27/26",
  status: "WIP",
  placeholder: body
    .filter((block): block is Extract<WritingBlock, { type: "paragraph" }> => block.type === "paragraph")
    .map((block) => block.text),
  body,
};

export default writing;
